import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  LeadCardCheckboxItemData,
  LeadCardUserDetailsData,
  LeadCardResponsibleEmployeeData,
} from '@app/components/LeadCard/data';

import { useLeadHistoryFilterSettings } from './useLeadHistoryFilterSettings';

interface UseLeadCardUserDetailsStateParams {
  resetKey: string | number | null;
  selectedLeadUserDetails: LeadCardUserDetailsData | null;
}

interface UseLeadCardUserDetailsStateResult {
  selectedEmployeeId: LeadCardResponsibleEmployeeData['id'] | null;
  selectEmployee(employeeId: LeadCardResponsibleEmployeeData['id']): void;
  selectResponsibleEmployee(
    employeeId: LeadCardResponsibleEmployeeData['id']
  ): void | Promise<void>;
  sourceItems: LeadCardCheckboxItemData[];
  toggleSource(itemId: LeadCardCheckboxItemData['id'], checked: boolean): void;
  selectAllSources(): void;
  clearSources(): void;
  actionItems: LeadCardCheckboxItemData[];
  toggleAction(itemId: LeadCardCheckboxItemData['id'], checked: boolean): void;
  selectAllActions(): void;
  clearActions(): void;
}

const EMPTY_CHECKBOX_ITEMS: LeadCardCheckboxItemData[] = [];

const areCheckboxItemsEqual = (
  left: LeadCardCheckboxItemData[],
  right: LeadCardCheckboxItemData[]
): boolean => {
  if (left === right) return true;
  if (left.length !== right.length) return false;

  return left.every((leftItem, index) => {
    const rightItem = right[index];

    return (
      rightItem !== undefined &&
      leftItem.id === rightItem.id &&
      leftItem.label === rightItem.label &&
      leftItem.checked === rightItem.checked
    );
  });
};

// Пункт отмечен, если его значения нет среди скрытых (us/11168: на бэке
// хранятся только снятые чекбоксы).
const applyHiddenIds = (
  items: LeadCardCheckboxItemData[],
  hiddenIds: string[]
): LeadCardCheckboxItemData[] => {
  const hidden = new Set(hiddenIds);

  return items.map((item) => ({
    ...item,
    checked: !hidden.has(item.id),
  }));
};

const toggleHiddenId = (
  hiddenIds: string[],
  itemId: LeadCardCheckboxItemData['id'],
  checked: boolean
): string[] => {
  if (checked) return hiddenIds.filter((id) => id !== itemId);

  return hiddenIds.includes(itemId) ? hiddenIds : [...hiddenIds, itemId];
};

// Настройки общие для всех лидов пользователя: «Все» и «Очистить» меняют
// только пункты текущего лида, скрытые значения других лидов остаются.
const showItems = (
  hiddenIds: string[],
  items: LeadCardCheckboxItemData[]
): string[] => {
  const itemIds = new Set(items.map(({ id }) => id));

  return hiddenIds.filter((id) => !itemIds.has(id));
};

const hideItems = (
  hiddenIds: string[],
  items: LeadCardCheckboxItemData[]
): string[] =>
  Array.from(new Set([...hiddenIds, ...items.map(({ id }) => id)]));

// Карточка пересобирает данные лида при каждом обновлении, а от этих
// списков зависит фильтрация ленты: при тех же пунктах отдаём прежний массив.
const useStableCheckboxItems = (
  items: LeadCardCheckboxItemData[]
): LeadCardCheckboxItemData[] => {
  const stableItemsRef = useRef(items);

  if (!areCheckboxItemsEqual(stableItemsRef.current, items)) {
    stableItemsRef.current = items;
  }

  return stableItemsRef.current;
};

export const useUserDetailsState = ({
  resetKey,
  selectedLeadUserDetails,
}: UseLeadCardUserDetailsStateParams): UseLeadCardUserDetailsStateResult => {
  const {
    defaultResponsibleEmployeeId = null,
    shouldPersistFilters = false,
    sourceItems: defaultSourceItems = EMPTY_CHECKBOX_ITEMS,
    actionItems: defaultActionItems = EMPTY_CHECKBOX_ITEMS,
  } = selectedLeadUserDetails ?? {};
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<
    LeadCardResponsibleEmployeeData['id'] | null
  >(defaultResponsibleEmployeeId ?? null);

  const { hiddenFilters, updateHiddenFilters } = useLeadHistoryFilterSettings({
    isEnabled: shouldPersistFilters,
  });

  useEffect(() => {
    setSelectedEmployeeId((prev) =>
      Object.is(prev, defaultResponsibleEmployeeId)
        ? prev
        : defaultResponsibleEmployeeId
    );
  }, [resetKey, defaultResponsibleEmployeeId]);

  const sourceItems = useStableCheckboxItems(
    useMemo(
      () => applyHiddenIds(defaultSourceItems, hiddenFilters.socialTypes),
      [defaultSourceItems, hiddenFilters.socialTypes]
    )
  );
  const actionItems = useStableCheckboxItems(
    useMemo(
      () => applyHiddenIds(defaultActionItems, hiddenFilters.actionIds),
      [defaultActionItems, hiddenFilters.actionIds]
    )
  );

  const selectEmployee = useCallback(
    (employeeId: LeadCardResponsibleEmployeeData['id']) => {
      setSelectedEmployeeId(employeeId);
    },
    []
  );

  const selectResponsibleEmployee =
    selectedLeadUserDetails?.onResponsibleChange ?? selectEmployee;

  const toggleSource = useCallback(
    (itemId: LeadCardCheckboxItemData['id'], checked: boolean) => {
      updateHiddenFilters((prev) => ({
        ...prev,
        socialTypes: toggleHiddenId(prev.socialTypes, itemId, checked),
      }));
    },
    [updateHiddenFilters]
  );

  const selectAllSources = useCallback(() => {
    updateHiddenFilters((prev) => ({
      ...prev,
      socialTypes: showItems(prev.socialTypes, defaultSourceItems),
    }));
  }, [defaultSourceItems, updateHiddenFilters]);

  const clearSources = useCallback(() => {
    updateHiddenFilters((prev) => ({
      ...prev,
      socialTypes: hideItems(prev.socialTypes, defaultSourceItems),
    }));
  }, [defaultSourceItems, updateHiddenFilters]);

  const toggleAction = useCallback(
    (itemId: LeadCardCheckboxItemData['id'], checked: boolean) => {
      updateHiddenFilters((prev) => ({
        ...prev,
        actionIds: toggleHiddenId(prev.actionIds, itemId, checked),
      }));
    },
    [updateHiddenFilters]
  );

  const selectAllActions = useCallback(() => {
    updateHiddenFilters((prev) => ({
      ...prev,
      actionIds: showItems(prev.actionIds, defaultActionItems),
    }));
  }, [defaultActionItems, updateHiddenFilters]);

  const clearActions = useCallback(() => {
    updateHiddenFilters((prev) => ({
      ...prev,
      actionIds: hideItems(prev.actionIds, defaultActionItems),
    }));
  }, [defaultActionItems, updateHiddenFilters]);

  return useMemo(
    () => ({
      selectedEmployeeId,
      selectEmployee,
      selectResponsibleEmployee,
      sourceItems,
      toggleSource,
      selectAllSources,
      clearSources,
      actionItems,
      toggleAction,
      selectAllActions,
      clearActions,
    }),
    [
      actionItems,
      clearActions,
      clearSources,
      selectAllActions,
      selectAllSources,
      selectEmployee,
      selectResponsibleEmployee,
      selectedEmployeeId,
      sourceItems,
      toggleAction,
      toggleSource,
    ]
  );
};
